import {
  Txn, FireSettings, MortgageSettings,
  SWR_MIN_BP, SWR_MAX_BP, SIM_MAX_MONTHS,
} from '../money.model';
import { spendableTxns, recentMonths } from '../dashboard/dashboard.aggregate';
import { monthlyPayment, amortize, balanceAtMonth } from '../mortgage/mortgage.calc';

const MONTHS_PER_YEAR = 12;
const SPEND_WINDOW_MONTHS = 12;

/**
 * Outflow over the last twelve COMPLETE months, or null when no spending
 * was recorded in the window.
 *
 * Whole months only: the current month is still accruing, so including it
 * would drag the figure down by an amount that shrinks as the month goes on.
 * Same reasoning as savingsRateChips.
 *
 * Null rather than zero for an empty window or one holding only income.
 * A false "spent nothing" would report a FIRE number of zero and a FIRE date
 * of today, which is worse than admitting there is nothing to go on.
 */
export function ledgerAnnualSpendCents(txns: Txn[], asOf: string): number | null {
  const currentMonth = asOf.slice(0, 7);
  const [lastCompleteMonth] = recentMonths(currentMonth, 2);
  const window = new Set(recentMonths(lastCompleteMonth, SPEND_WINDOW_MONTHS));

  let total = 0;
  let seen = false;

  spendableTxns(txns ?? []).forEach(t => {
    if (!window.has(t.date.slice(0, 7))) return;
    if (t.amount < 0) {
      seen = true;
      total += -t.amount;
    }
  });

  return seen ? total : null;
}

/**
 * The spend figure broken into its parts, so the screen can render the
 * arithmetic rather than just its result.
 */
export interface SpendBreakdown {
  /** What the nest egg must sustain forever. Never negative. */
  totalCents: number;
  /** The ledger's own figure, reported even when the source is manual. */
  ledgerCents: number | null;
  /** Twelve months of derived P&I, or zero when the deduction is off. */
  mortgageDeductionCents: number;
}

/**
 * Spending that outlives the mortgage.
 *
 * The deduction is only correct if the mortgage payment is actually in the
 * imported ledger, which is why `deductMortgagePI` is a user toggle and why
 * the parts are returned separately for display.
 *
 * Escrow deliberately stays in: property tax and insurance do not stop at
 * payoff, and only the P&I portion is subtracted.
 */
export function perpetualAnnualSpend(
  fire: FireSettings, mortgage: MortgageSettings, txns: Txn[], asOf: string,
): SpendBreakdown {
  const ledgerCents = ledgerAnnualSpendCents(txns, asOf);

  const base = fire.spendSource === 'manual'
    ? Math.max(fire.manualAnnualSpendCents, 0)
    : Math.max(ledgerCents ?? 0, 0);

  const mortgageDeductionCents = fire.deductMortgagePI
    ? monthlyPayment(mortgage) * MONTHS_PER_YEAR
    : 0;

  return {
    totalCents: Math.max(base - mortgageDeductionCents, 0),
    ledgerCents,
    mortgageDeductionCents,
  };
}

const BASIS_POINTS_PER_UNIT = 10000;

export interface SimPoint {
  /** 0-based from today. */
  month: number;
  portfolioCents: number;
  targetCents: number;
}

export interface SimResult {
  /** Always the full horizon, never truncated at the FIRE month: Coast FIRE
   *  reads a target at an age that may fall well after that date. */
  points: SimPoint[];
  /** Months to the first crossing, or null when it never happens. */
  fireMonth: number | null;
  /** Months until the loan clears, or null with no loan or a stalled one. */
  payoffMonth: number | null;
  /** The payment never covers one month of interest. */
  loanStalled: boolean;
  /** Contribution plus loan payment plus extra — identical across the two
   *  scenarios, which is what makes the comparison fair. */
  monthlyOutlayCents: number;
}

/** A zero withdrawal rate is an infinite target; the ceiling keeps the tool to
 *  rates arithmetic can be honest about. */
export function clampSwr(bp: number): number {
  if (!Number.isFinite(bp)) return SWR_MIN_BP;
  return Math.min(Math.max(Math.round(bp), SWR_MIN_BP), SWR_MAX_BP);
}

/**
 * Walk the portfolio and the target forward month by month.
 *
 *   target(month) = perpetualAnnualSpend x (10000 / swrBp) + mortgageBalance(month)
 *
 * The nest egg must sustain spending that lasts forever, PLUS the mortgage,
 * which does not — at any month the loan could simply be paid off outright.
 * That is conservative, it is one line of arithmetic rather than a
 * present-value calculation, and it is the reason the target slopes DOWN as
 * the loan amortizes while the portfolio climbs. The FIRE date is where they
 * cross.
 *
 * `extraToMortgage` decides where `mortgage.extraMonthlyCents` is applied — to
 * the loan, or added to the monthly contribution. Never both, never dropped.
 *
 * The snowball is not optional. Once the loan is gone the payment has to go
 * somewhere, and omitting it would make the pay-down scenario lose a race it
 * might genuinely win — actively misleading rather than merely incomplete.
 */
export function simulate(
  fire: FireSettings, mortgage: MortgageSettings,
  opts: { extraToMortgage: boolean }, perpetualAnnualSpendCents: number,
): SimResult {
  const extra = Math.max(mortgage.extraMonthlyCents, 0);
  const loan: MortgageSettings = {
    ...mortgage, extraMonthlyCents: opts.extraToMortgage ? extra : 0,
  };

  const schedule = amortize(loan);
  const payment = monthlyPayment(loan);
  const loanStalled = mortgage.principalCents > 0 && schedule.length === 0;

  // What the loan consumes each month while it lives, and what comes free the
  // month it dies.
  const loanOutlay = payment + (opts.extraToMortgage ? extra : 0);
  const baseContrib = fire.monthlyContribCents + (opts.extraToMortgage ? 0 : extra);

  const multiple = BASIS_POINTS_PER_UNIT / clampSwr(fire.swrBasisPoints);
  const perpetualTarget = Math.round(Math.max(perpetualAnnualSpendCents, 0) * multiple);
  const monthlyRate = fire.rateBasisPoints / BASIS_POINTS_PER_UNIT / 12;

  let portfolio = fire.portfolioCents;
  let fireMonth: number | null = null;
  const points: SimPoint[] = [];

  for (let month = 0; month <= SIM_MAX_MONTHS; month++) {
    const balance = balanceAtMonth(schedule, mortgage.principalCents, month);
    const targetCents = perpetualTarget + balance;

    const portfolioCents = Math.round(portfolio);
    points.push({ month, portfolioCents, targetCents });
    if (fireMonth === null && portfolioCents >= targetCents) fireMonth = month;

    const contribution = balance > 0 ? baseContrib : baseContrib + loanOutlay;
    portfolio = portfolio * (1 + monthlyRate) + contribution;
  }

  // `amortize` can truncate at SIM_MAX_MONTHS with a nonzero balance still
  // owed (a term longer than the horizon). That is not a payoff — only a
  // schedule whose final row actually clears the balance counts as one.
  const paidOff = schedule.length > 0
    && schedule[schedule.length - 1].balanceCents === 0;

  return {
    points, fireMonth, loanStalled,
    payoffMonth: paidOff ? schedule.length : null,
    monthlyOutlayCents: baseContrib + loanOutlay,
  };
}

export interface Comparison {
  /** The extra goes at the loan; contributions stay at base. */
  toMortgage: SimResult;
  /** The loan runs on schedule; the extra is invested instead. */
  toInvest: SimResult;
  /** Months the mortgage-first plan reaches FI SOONER. Negative means
   *  investing wins; null when either scenario never reaches FI. */
  diffMonths: number | null;
  /**
   * The extra monthly payment as it stood when this comparison ran, clamped
   * to zero like the simulations themselves (see `simulate`'s `extra`).
   *
   * A background push can update `mortgage.extraMonthlyCents` between runs
   * while this stale `Comparison` is still on screen. Reading the live value
   * in prose built from THIS comparison's numbers would pair a new dollar
   * amount with an old month gap; snapshotting it here keeps the sentence
   * internally consistent until the next Recalculate.
   */
  extraMonthlyCentsAtRun: number;
}

/** Both scenarios, run on the same total monthly outlay. */
export function compareScenarios(
  fire: FireSettings, mortgage: MortgageSettings, perpetualAnnualSpendCents: number,
): Comparison {
  const toMortgage = simulate(fire, mortgage, { extraToMortgage: true },
                              perpetualAnnualSpendCents);
  const toInvest = simulate(fire, mortgage, { extraToMortgage: false },
                            perpetualAnnualSpendCents);

  const diffMonths = toMortgage.fireMonth !== null && toInvest.fireMonth !== null
    ? toInvest.fireMonth - toMortgage.fireMonth
    : null;

  return {
    toMortgage, toInvest, diffMonths,
    extraMonthlyCentsAtRun: Math.max(mortgage.extraMonthlyCents, 0),
  };
}

/** Which prose branch `verdict` should render, derived purely from the two
 *  simulations' reachability. Kept here rather than in the component, which
 *  has no spec file, so all three outcomes can be pinned by a test. */
export type ComparisonOutcome = 'neither' | 'mortgage-only' | 'invest-only' | 'gap';

export function classifyComparison(c: Comparison): ComparisonOutcome {
  const { toMortgage, toInvest } = c;
  if (toMortgage.fireMonth === null && toInvest.fireMonth === null) return 'neither';
  if (toInvest.fireMonth === null) return 'mortgage-only';
  if (toMortgage.fireMonth === null) return 'invest-only';
  return 'gap';
}

/**
 * What the portfolio must hold TODAY to reach the target by `coastAge` with no
 * further contributions.
 *
 * The target is read straight out of the simulation's own points, and
 * discounted back with the same MONTHLY compounding `simulate` grows the
 * portfolio at (rate / 12 per month, over `month` months) rather than an
 * effective annual rate over years. That match is what lets this figure,
 * grown forward through `simulate`'s own math with no further contributions,
 * land back on the target it was discounted from.
 */
export function coastNumberCents(fire: FireSettings, points: SimPoint[]): number | null {
  const years = fire.coastAge - fire.currentAge;
  if (!(years > 0)) return null;

  const month = Math.round(years * 12);
  if (month >= points.length) return null;

  const rate = fire.rateBasisPoints / BASIS_POINTS_PER_UNIT;
  return Math.round(points[month].targetCents / Math.pow(1 + rate / 12, month));
}
